import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { getLocalItem } from '../../utils/storage';

// Mandatory training-video modal. Rendered ONLY when the backend
// (get_mechanic_status -> enforcement_state) has an active enforcement for this
// mechanic. For every other mechanic enforcement_state is empty, so this is
// never mounted and the dashboard is unchanged.
//
// Flow: (1) one-time language pick if none set -> (2) play the step video in
// that language -> (3) "Done" enabled only after the video finishes -> clears
// the enforcement on the server so it doesn't reappear.

const LANGS = [
  { code: 'hi', label: 'हिन्दी', sub: 'Hindi' },
  { code: 'mr', label: 'मराठी', sub: 'Marathi' },
  { code: 'ta', label: 'தமிழ்', sub: 'Tamil' },
  { code: 'te', label: 'తెలుగు', sub: 'Telugu' },
  { code: 'bn', label: 'বাংলা', sub: 'Bengali' },
];

const authHeaders = () => {
  const token = getLocalItem('authToken');
  return token ? { Authorization: 'Bearer ' + token } : {};
};

const S = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.92)', zIndex: 100000,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '16px',
  },
  card: {
    background: '#fff', borderRadius: '20px', width: '100%', maxWidth: '440px',
    padding: '22px 20px', textAlign: 'center', boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
    maxHeight: '92vh', overflowY: 'auto',
  },
  badge: {
    display: 'inline-block', background: '#fde8e8', color: '#f42222',
    fontWeight: 700, fontSize: '12px', letterSpacing: '0.5px',
    padding: '5px 12px', borderRadius: '20px', textTransform: 'uppercase',
    marginBottom: '12px',
  },
  title: { margin: '0 0 6px', fontSize: '20px', fontWeight: 700, color: '#1a1a1a' },
  sub: { margin: '0 0 18px', fontSize: '14px', color: '#666', lineHeight: 1.5 },
  langBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    width: '100%', padding: '15px 18px', margin: '0 0 10px',
    border: '2px solid #eee', borderRadius: '14px', background: '#fff',
    fontSize: '18px', fontWeight: 600, color: '#1a1a1a', cursor: 'pointer',
  },
  langSub: { fontSize: '13px', color: '#999', fontWeight: 500 },
  video: { width: '100%', borderRadius: '14px', background: '#000', marginBottom: '14px' },
  doneBtn: (enabled) => ({
    width: '100%', padding: '15px', border: 'none', borderRadius: '14px',
    background: enabled ? '#f42222' : '#f0c4c4', color: '#fff',
    fontSize: '17px', fontWeight: 700, cursor: enabled ? 'pointer' : 'not-allowed',
    minHeight: '52px',
  }),
  hint: { fontSize: '13px', color: '#999', margin: '0 0 12px' },
};

class TrainingEnforcementModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      lang: props.preferredLang || null,
      videoEnded: false,
      saving: false,
      finishing: false,
      error: '',
    };
  }

  chooseLanguage = async (code) => {
    this.setState({ saving: true, error: '' });
    try {
      await axios.post(
        'https://ridenrepair.com/auto/set_mechanic_language',
        { mechanic_phone: this.props.mechanicPhone, language: code },
        { headers: authHeaders() }
      );
      this.setState({ lang: code, saving: false });
    } catch (e) {
      // Even if the save fails, let them proceed in the chosen language for
      // this session (don't trap the mechanic). It will re-ask next time.
      this.setState({ lang: code, saving: false });
    }
  };

  onVideoEnded = () => this.setState({ videoEnded: true });

  finish = async () => {
    if (!this.state.videoEnded || this.state.finishing) return;
    this.setState({ finishing: true });
    const { enforcementState, mechanicPhone, onDismiss } = this.props;
    try {
      await axios.post(
        'https://ridenrepair.com/auto/training_watch_progress',
        {
          mechanic_phone: mechanicPhone,
          skill: (enforcementState && enforcementState.skill) || 'step1_accept',
          completed: true,
          lang: this.state.lang,
        },
        { headers: authHeaders() }
      );
    } catch (e) { /* non-blocking — dismiss regardless so we never trap them */ }
    if (typeof onDismiss === 'function') onDismiss();
  };

  render() {
    const { enforcementState } = this.props;
    if (!enforcementState || !enforcementState.skill) return null;
    const { lang, videoEnded, saving, finishing } = this.state;
    const category = (enforcementState && enforcementState.video_category) || 'notification';
    const videoUrl = lang ? `/mechanic-training/${category}/${lang}.mp4` : null;

    const body = (
      <div style={S.overlay}>
        <div style={S.card}>
          <div style={S.badge}>Training</div>
          {!lang ? (
            <>
              <h3 style={S.title}>Choose your language</h3>
              <p style={S.sub}>अपनी भाषा चुनें · Select the language for your training videos</p>
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  style={S.langBtn}
                  disabled={saving}
                  onClick={() => this.chooseLanguage(l.code)}
                >
                  <span>{l.label}</span>
                  <span style={S.langSub}>{l.sub}</span>
                </button>
              ))}
            </>
          ) : (
            <>
              <h3 style={S.title}>{enforcementState.display_name || 'Watch this short video'}</h3>
              <p style={S.sub}>
                Please watch this short video fully to continue.
                <br />कृपया जारी रखने के लिए यह वीडियो पूरा देखें।
              </p>
              <video
                style={S.video}
                src={videoUrl}
                controls
                playsInline
                preload="auto"
                onEnded={this.onVideoEnded}
              />
              {!videoEnded ? (
                <p style={S.hint}>
                  ▶ Please watch the <b>full</b> video. The <b>“Done”</b> button will
                  appear here once the video finishes — then tap it to continue.
                  <br />कृपया पूरा वीडियो देखें। वीडियो खत्म होने पर “Done” बटन यहाँ दिखेगा।
                </p>
              ) : (
                <button
                  style={S.doneBtn(!finishing)}
                  disabled={finishing}
                  onClick={this.finish}
                >
                  {finishing ? 'Please wait…' : "✓ Done — I've watched it"}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );

    return ReactDOM.createPortal(body, document.body);
  }
}

export default TrainingEnforcementModal;
